import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import TablePagination from "./TablePagination";
import { getLocations, saveLocation, deleteLocation } from "../Apis/MasterAPIs";
import usePermissions from "../hook/userPermission";

const LocationLayer = () => {
  const { hasPermission } = usePermissions();
  const [locations, setLocations] = useState([]);
  const [newLocation, setNewLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchLocations = async () => {
    setLoading(true);
    const params = {
      page: currentPage,
      limit: rowsPerPage,
    };
    if (debouncedSearchTerm) params.search = debouncedSearchTerm;

    const res = await getLocations(params);
    if (res.status) {
      const data = res.response?.data || res.response;
      setLocations(Array.isArray(data) ? data : []);
      setTotalRecords(res.response?.total || (Array.isArray(data) ? data.length : 0));
    }
    setLoading(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchLocations();
  }, [debouncedSearchTerm, currentPage, rowsPerPage]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!newLocation.trim()) {
      setError("Location name is Required");
      return;
    }

    const res = await saveLocation({ name: newLocation.trim() });
    if (res.status) {
      toast.success("Location Saved Successfully");
      setNewLocation("");
      setError("");
      fetchLocations();
    }
  };

  const handleDeleteClick = (loc) => {
    setItemToDelete(loc);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsUpdating(true);
    const res = await deleteLocation(itemToDelete._id || itemToDelete.id);
    if (res.status) {
      toast.success("Location Deleted Successfully");
      fetchLocations();
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
    setIsUpdating(false);
  };

  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  return (
    <div className="row gy-4">
      <div className="col-12">
        <div className="card h-100 p-0 radius-12 shadow-sm">
          <div
            className="card-header border-bottom py-12 px-24 d-flex align-items-center justify-content-between"
            style={{
              background:
                "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
            }}
          >
            <h6 className="text-white mb-0">Location List</h6>
            <div className="d-flex align-items-center gap-3">
              <div className="navbar-search position-relative">
                <input
                  type="text"
                  className="form-control radius-8 ps-40 py-10 bg-white border-0"
                  style={{ height: "44px" }}
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(0);
                  }}

                />
                <Icon
                  icon="solar:magnifer-linear"
                  className="position-absolute text-secondary-light"
                  style={{
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "18px"
                  }}
                />
              </div>
            </div>
          </div>
          <div className="card-body">
            {hasPermission("location", "add") && (
              <div style={{ marginBottom: "20px" }}>
                <form
                  onSubmit={handleSave}
                  className="d-flex gap-3 align-items-start flex-wrap"
                >
                  <div className="flex-grow-1" style={{ minWidth: "250px" }}>
                    <label className="form-label fw-bold">
                      Location Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control radius-8 ${error ? "is-invalid" : ""}`}
                      placeholder="Enter Location (e.g. Coimbatore)"
                      value={newLocation}
                      onChange={(e) => {
                        setNewLocation(e.target.value);
                        if (error) setError("");
                      }}
                    />
                    {error && <div className="invalid-feedback">{error}</div>}
                  </div>
                  <button
                    type="submit"
                    className="btn radius-8 px-32 py-11 d-flex align-items-center gap-2 mt-32"
                    style={{
                      backgroundColor: "#f7e7da",
                      color: "black",
                      fontWeight: "600",
                    }}
                  >
                    <Icon icon="ic:baseline-plus" className="text-xl" />
                    Add New Location
                  </button>
                </form>
              </div>
            )}
            <div
              className="table-responsive scroll-sm"
              style={{ maxHeight: "400px", overflowY: "auto" }}
            >
              <table className="table bordered-table sm-table mb-0">
                <thead
                  style={{
                    backgroundColor: "#f7e7da",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <tr>
                    <th scope="col" style={{ color: "black", fontWeight: "bold", width: "100px" }}>S.No</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Location Name</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold", width: "150px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="3" className="text-center py-24">Loading...</td></tr>
                  ) : locations.length > 0 ? (
                    locations.map((loc, index) => (
                      <tr key={loc._id || loc.id}>
                        <td>{currentPage * rowsPerPage + index + 1}</td>
                        <td>
                          <span className="text-md mb-0 fw-medium text-secondary-light">
                            {loc.name}
                          </span>
                        </td>
                        <td>
                          {hasPermission("location", "delete") && (
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(loc)}
                              title="Delete Location"
                              className="bg-danger-focus text-danger-600 bg-hover-danger-200 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            >
                              <Icon icon="lucide:trash-2" className="text-xl" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="3" className="text-center py-24 text-secondary-light">No locations found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(0);
              }}
              totalRecords={totalRecords}
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon icon="solar:trash-bin-trash-bold" className="text-danger-600 text-xxl" />
            </div>
          </div>
          <h5 className="mb-2">Are you sure?</h5>
          <p className="text-secondary-light mb-4">
            Do you want to delete location "{itemToDelete?.name}"?
          </p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button variant="outline-secondary" className="px-32 radius-8" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button className="px-32 radius-8 border-0" onClick={confirmDelete} disabled={isUpdating} style={{ backgroundColor: "#ed1c24", color: "white" }}>
              {isUpdating ? "Deleting..." : "Confirm"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LocationLayer;
